<template>
  <div class="search-articles">
    <PrepeersBreadcrumb
      auto
      section="recherche" />

    <!-- ── HERO + H1 ─────────────────────────────────────────────── -->
    <section class="hero-section">
      <div class="container">
        <h1 class="hero-section__h1">{{ seoPayload?.h1 }}</h1>
        <div
          v-if="seoPayload?.introduction"
          class="hero-section__intro"
          v-html="formatSeoIntro(seoPayload.introduction)" />
        <div class="hero-section__line" />
      </div>
    </section>

    <!-- ── BARRE DE RECHERCHE ───────────────────────────────────── -->
    <section class="search-articles__toolbar-section">
      <div class="container search-articles__toolbar-inner">
        <PrePeersSearchBar
          v-model="searchDraft"
          :placeholder="t('searchArticles.searchPlaceholder')"
          :submit-label="t('searchArticles.searchSubmit')"
          @search="onSubmitSearch" />
      </div>
    </section>

    <!-- ── CATÉGORIES ───────────────────────────────────────────── -->
    <section class="level-nav-section search-articles__categories">
      <div class="container">
        <h2 class="level-nav__title level-nav__title--left">
          {{ hubNavTitle }}
        </h2>
        <div
          class="level-nav__list search-articles__category-list"
          role="tablist"
          aria-label="Catégories d'articles">
          <PrePeersNavLink
            :to="localePath(categoryRouteFor(null))"
            :active="activeCategorySlug === null"
            role="tab"
            :aria-selected="activeCategorySlug === null">
            {{ t('searchArticles.categoryAll') }}
          </PrePeersNavLink>
          <PrePeersNavLink
            v-for="tab in categoryTabsFiltered"
            :key="tab.slug ?? 'x'"
            :to="localePath(categoryRouteFor(tab.slug))"
            :active="activeCategorySlug === tab.slug"
            role="tab"
            :aria-selected="activeCategorySlug === tab.slug">
            {{ tab.label }}
          </PrePeersNavLink>
        </div>
      </div>
    </section>

    <!-- ── COMPTEUR ─────────────────────────────────────────────── -->
    <section class="search-articles__count-section">
      <div class="container">
        <p class="search-articles__count">
          {{ articleCountLabel }}
        </p>
      </div>
    </section>

    <!-- ── GRILLE ARTICLES ─────────────────────────────────────── -->
    <section class="search-articles__articles-band">
      <div class="container">
        <div
          v-if="displayedArticles.length"
          class="search-articles__article-grid">
          <UiArticleCard
            v-for="article in displayedArticles"
            :key="article.id"
            :article="article" />
        </div>
        <p
          v-else
          class="search-articles__empty">
          {{ t('searchArticles.noResults') }}
        </p>
      </div>
    </section>

    <!-- ── CTA ───────────────────────────────────────────────────── -->
    <section
      v-if="!props.isLoggedIn"
      class="cta-section">
      <div class="container">
        <div class="cta-card">
          <div class="cta-card__avatar">
            <NuxtImg
              src="/images/lola-avatar.png"
              alt="Lola"
              class="cta-card__avatar-img"
              width="72"
              height="72"
              loading="lazy"
              decoding="async"
              format="webp" />
          </div>
          <h3 class="cta-card__title">{{ t('searchArticles.ctaTitle') }}</h3>
          <p class="cta-card__desc">{{ t('searchArticles.ctaDesc') }}</p>
          <NuxtLink
            :to="localePath('/discuter')"
            class="cta-card__btn"
            >{{ t('searchArticles.ctaBtn') }}</NuxtLink
          >
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import type { LocationQueryRaw, LocationQueryValue } from 'vue-router'

import PrePeersNavLink from '~/components/core/PrePeersNavLink.vue'
import PrePeersSearchBar from '~/components/form/PrePeersSearchBar.vue'
import PrepeersBreadcrumb from '~/components/navigation/PrepeersBreadcrumb.vue'
import { useBlogSearchHub } from '~/composables/useBlogSearchHub'
import type { BlogSearchCategoryTab } from '~/types/BlogSearchHub'
import type { BlogArticleObj } from '~/types/school'
import type { SearchArticlesCategoryRoute, SearchArticlesProps } from '~/types/SearchArticles'
import { formatSeoIntro } from '~/utils/helpers'

// ── Props ─────────────────────────────────────────────────────────────

/**
 * Définit les props du composant SearchArticles.
 */
const props: SearchArticlesProps = defineProps({
  isLoggedIn: {
    type: Boolean,
    default: false,
  },
})

// ── Composables (i18n, route, données hub recherche) ───────────────────

const { t } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const router = useRouter()

const {
  seoPayload,
  hubChildren,
  categoryTabs,
  categorySlug: activeCategorySlug,
  basePath,
  filterArticles,
} = useBlogSearchHub()

// ── Refs ──────────────────────────────────────────────────────────────

const searchDraft: Ref<string> = ref<string>('')
const appliedSearch: Ref<string> = ref<string>('')

// ── ComputedRef ───────────────────────────────────────────────────────

const hubNavTitle: ComputedRef<string> = computed((): string => {
  const titleFromHub: string | undefined = hubChildren.value.Title?.trim()
  if (titleFromHub) return titleFromHub
  return t('searchArticles.categoriesTitle')
})

/** Onglets hors entrée « Tout » dupliquée (le composable inclut déjà Tout avec label vide). */
const categoryTabsFiltered: ComputedRef<BlogSearchCategoryTab[]> = computed(
  (): BlogSearchCategoryTab[] =>
    categoryTabs.value.filter((tab: BlogSearchCategoryTab) => tab.slug !== null),
)

const displayedArticles: ComputedRef<BlogArticleObj[]> = computed((): BlogArticleObj[] =>
  filterArticles(appliedSearch.value),
)

const articleCountLabel: ComputedRef<string> = computed((): string => {
  const n: number = displayedArticles.value.length
  if (n === 0) return t('searchArticles.articleCountZero')
  if (n === 1) return t('searchArticles.articleCountOne')
  return t('searchArticles.articleCountMany', { n })
})

const seoTitle: ComputedRef<string | undefined> = computed(
  (): string | undefined => seoPayload.value?.title,
)

const seoDescription: ComputedRef<string | undefined> = computed(
  (): string | undefined => seoPayload.value?.description,
)

const seoOgTitle: ComputedRef<string | undefined> = computed(
  (): string | undefined => seoPayload.value?.h1,
)

const seoOgDescription: ComputedRef<string | undefined> = computed(
  (): string | undefined => seoPayload.value?.description,
)

// ── Fonctions ─────────────────────────────────────────────────────────

/**
 * Lit un paramètre de query Vue Router en chaîne (première valeur si tableau).
 * @param {LocationQueryValue | LocationQueryValue[] | undefined} param - Valeur brute de `route.query`.
 * @returns {string} Chaîne normalisée (vide si absente).
 */
function readRouteQueryParam(param: LocationQueryValue | LocationQueryValue[] | undefined): string {
  const value = Array.isArray(param) ? param[0] : param
  if (value == null || value === '') return ''
  return String(value)
}

/**
 * Synchronise le champ de recherche et l'état appliqué avec le paramètre d'URL `q`.
 * Lit `route.query.q`, normalise en chaîne, puis met à jour `searchDraft` et `appliedSearch`.
 * @returns {void}
 */
function syncSearchFromRoute(): void {
  const v: string = readRouteQueryParam(route.query.q)
  searchDraft.value = v
  appliedSearch.value = v
}

/**
 * Construit la route (path + query) pour un filtre de catégorie, en conservant `q` si présent dans l'URL.
 * @param {string | null} slug - Slug catégorie ; `null` pour l'onglet « Tout ».
 * @returns {SearchArticlesCategoryRoute} Objet passé à `localePath(...)`.
 */
function categoryRouteFor(slug: string | null): SearchArticlesCategoryRoute {
  const q: Record<string, string> = {}
  const rq: string = readRouteQueryParam(route.query.q)
  if (rq) q.q = rq
  if (slug) q.cat = slug
  return { path: basePath, query: q }
}

/**
 * Soumet la recherche texte : met à jour l'état local et la query `q` via le routeur.
 * @param {string} value - Texte de recherche (déjà trim côté barre d'envoi).
 * @returns {void}
 */
function onSubmitSearch(value: string): void {
  appliedSearch.value = value
  const nextQuery: LocationQueryRaw = { ...route.query }
  if (value) nextQuery.q = value
  else delete nextQuery.q
  void router.push({ path: route.path, query: nextQuery })
}

// ── Watch ───────────────────────────────────────────────────────────────

watch(
  (): string => readRouteQueryParam(route.query.q),
  (): void => {
    syncSearchFromRoute()
  },
  { immediate: true },
)

// ── SEO meta ──────────────────────────────────────────────────────────

useSeoMeta({
  title: seoTitle,
  description: seoDescription,
  ogTitle: seoOgTitle,
  ogDescription: seoOgDescription,
  ogType: 'website',
})
</script>

<style scoped>
.search-articles__toolbar-section {
  padding: var(--space-10) var(--container-pad) var(--space-6);
}

.search-articles__toolbar-inner {
  max-width: var(--container-md);
}

.search-articles__categories {
  padding-top: var(--space-4);
}

.level-nav__title--left {
  text-align: left;
  margin-left: 0;
  margin-right: auto;
  max-width: var(--container-xl);
}

.search-articles__category-list {
  justify-content: flex-start;
}

.search-articles__count-section {
  padding: 0 var(--container-pad) var(--space-8);
}

.search-articles__count {
  margin: 0;
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--text-primary);
  max-width: var(--container-xl);
}

.search-articles__articles-band {
  padding: var(--space-12) var(--container-pad) var(--space-20);
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.12) 0%,
    rgba(0, 0, 0, 0.22) 40%,
    rgba(0, 0, 0, 0.16) 100%
  );
  border-top: 1px solid var(--border-subtle);
}

.search-articles__article-grid {
  display: grid;
  grid-template-columns: 1fr;
  align-items: stretch;
  gap: var(--space-6);
  max-width: var(--container-xl);
  margin: 0 auto;
}

@media (min-width: 640px) {
  .search-articles__article-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .search-articles__article-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.search-articles__empty {
  text-align: center;
  color: var(--text-secondary);
  font-size: var(--text-md);
  margin: var(--space-12) 0;
}
</style>
